import axiosInstance from '@/axiosInstance';
import type { AxiosResponse } from 'axios';
import type { Tenant, TenantSite } from './types';

// All endpoints require the sysadmin privilege (PR-573).
export default {
  async getAll(): Promise<AxiosResponse<Tenant[]>> {
    try {
      const response = await axiosInstance.get(`/api/Tenant/GetAll`);
      return response;
    } catch (error) {
      console.error('Error loading tenants:', error);
      throw error;
    }
  },
  async getByName(name: string): Promise<AxiosResponse<Tenant>> {
    try {
      const response = await axiosInstance.get(`/api/Tenant/${encodeURIComponent(name)}`);
      return response;
    } catch (error) {
      console.error('Error loading tenant:', error);
      throw error;
    }
  },
  // SaveTenant is an upsert keyed on tenantName — pass createOnly on the create path so an
  // existing tenant is never silently overwritten (409 instead).
  async saveTenant(tenant: Tenant, createOnly = false): Promise<AxiosResponse<Tenant>> {
    try {
      const response = await axiosInstance.post(`/api/Tenant/SaveTenant`, tenant, {
        params: createOnly ? { createOnly: true } : {},
      });
      return response;
    } catch (error) {
      console.error('Error saving tenant:', error);
      throw error;
    }
  },
  // Only tenants with no site and no user role assignments can be renamed (409 otherwise).
  async renameTenant(name: string, newName: string): Promise<AxiosResponse<Tenant>> {
    try {
      const response = await axiosInstance.post(
        `/api/Tenant/${encodeURIComponent(name)}/Rename`,
        null,
        { params: { newName } },
      );
      return response;
    } catch (error) {
      console.error('Error renaming tenant:', error);
      throw error;
    }
  },
  // Only tenants with no site and no user role assignments can be deleted (409 otherwise).
  async deleteTenant(name: string): Promise<AxiosResponse<void>> {
    try {
      const response = await axiosInstance.delete(`/api/Tenant/${encodeURIComponent(name)}`);
      return response;
    } catch (error) {
      console.error('Error deleting tenant:', error);
      throw error;
    }
  },
  async activate(name: string): Promise<AxiosResponse<void>> {
    try {
      const response = await axiosInstance.patch(`/api/Tenant/${encodeURIComponent(name)}/Activate`);
      return response;
    } catch (error) {
      console.error('Error activating tenant:', error);
      throw error;
    }
  },
  async deactivate(name: string): Promise<AxiosResponse<void>> {
    try {
      const response = await axiosInstance.patch(`/api/Tenant/${encodeURIComponent(name)}/Deactivate`);
      return response;
    } catch (error) {
      console.error('Error deactivating tenant:', error);
      throw error;
    }
  },
  // Returns the tenant's updated site list.
  async assignSites(name: string, siteCodes: string[]): Promise<AxiosResponse<TenantSite[]>> {
    try {
      const response = await axiosInstance.post(`/api/Tenant/${encodeURIComponent(name)}/Sites`, siteCodes);
      return response;
    } catch (error) {
      console.error('Error assigning sites:', error);
      throw error;
    }
  },
  // Reassigns the site to the catch-all tenant; returns the tenant's updated site list.
  async unassignSite(name: string, siteCode: string): Promise<AxiosResponse<TenantSite[]>> {
    try {
      const response = await axiosInstance.delete(
        `/api/Tenant/${encodeURIComponent(name)}/Sites/${encodeURIComponent(siteCode)}`,
      );
      return response;
    } catch (error) {
      console.error('Error unassigning site:', error);
      throw error;
    }
  },
};
